<script setup lang="ts">
import { computed, onMounted, onScopeDispose, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import CollapsibleSection from '@/components/CollapsibleSection.vue'
import { useSyncActivityStore } from '@/stores/syncActivityStore'
import { useUIStore } from '@/stores/uiStore'
import { useLocalStorage } from '@/lib/utils/useLocalStorage'
import { timeAgo } from '@/lib/utils/timeAgo'
import { SYNC_KIND_ICON, SYNC_STATUS_VISUAL } from '@/lib/utils/syncActivityVisual'
import type { SyncActivity } from '@/lib/types'

/**
 * Bottom-right sync pane: what is being synced to the tenant's lockers and how
 * far it got. Shown only while something syncs, and for SYNC_PANE_LINGER_MS
 * after a sync settles to show its result; hidden otherwise. Hovering pauses
 * that countdown and leaving resumes what was left, as the toasts do
 * (`notificationStore.pauseTimeout` / `resumeTimeout`). Collapsed = one
 * line (the newest sync in progress, or the result that just settled);
 * expanded = the recent history, scrollable. Bounded to `<main>` like
 * `<Notification />`; sits under the right drawer (z-10) and slides left with
 * the desktop push so it never covers it.
 */
const { t } = useI18n()
const store = useSyncActivityStore()
const ui = useUIStore()
const open = useLocalStorage<boolean>('lockerserver:sync-pane-open', false)

/** How long a settled sync's result stays up once nothing else is syncing. */
const SYNC_PANE_LINGER_MS = 5_000
const lingering = ref(false)
let lingerTimer: ReturnType<typeof setTimeout> | null = null
let lingerStartedAt = 0
let lingerRemaining = 0
let hovered = false

function armLinger(ms: number): void {
  lingerStartedAt = Date.now()
  lingerRemaining = ms
  lingerTimer = setTimeout(() => {
    lingerTimer = null
    lingering.value = false
  }, ms)
}

watch(
  () => store.recentlySettled,
  (settled) => {
    if (settled === null) return
    lingering.value = true
    if (lingerTimer !== null) clearTimeout(lingerTimer)
    lingerTimer = null
    lingerRemaining = SYNC_PANE_LINGER_MS
    if (!hovered) armLinger(SYNC_PANE_LINGER_MS)
  },
)

function pauseHide(): void {
  hovered = true
  if (lingerTimer === null) return
  clearTimeout(lingerTimer)
  lingerTimer = null
  lingerRemaining = Math.max(0, lingerRemaining - (Date.now() - lingerStartedAt))
}

function resumeHide(): void {
  hovered = false
  if (!lingering.value || lingerTimer !== null) return
  if (lingerRemaining <= 0) {
    lingering.value = false
    return
  }
  armLinger(lingerRemaining)
}
const visible = computed(() => store.current !== null || (lingering.value && store.recentlySettled !== null))

// Relative times re-render on this tick; syncs in flight re-render on every ack anyway.
const now = ref(Date.now())
const clock = setInterval(() => (now.value = Date.now()), 30_000)
onScopeDispose(() => {
  clearInterval(clock)
  if (lingerTimer !== null) clearTimeout(lingerTimer)
  store.stop()
})
onMounted(() => store.start())

const percent = (a: SyncActivity): number => Math.round((a.acked_count / Math.max(a.target_count, 1)) * 100)
function ago(iso: string): string {
  // Reading `now` subscribes the render to the 30 s tick.
  if (now.value === 0) return ''
  return timeAgo(iso)
}

const moreActive = computed(() => Math.max(store.active.length - 1, 0))

function meta(a: SyncActivity): string {
  const parts = [t('syncActivity.lockersProgress', { acked: a.acked_count, total: a.target_count }, a.target_count)]
  const missing = a.target_count - a.acked_count - a.failed_count
  if (a.failed_count > 0) parts.push(t('syncActivity.failedCount', { count: a.failed_count }))
  if (a.status !== 'in_progress' && missing > 0) parts.push(t('syncActivity.offline', { count: missing }))
  parts.push(a.actor)
  return parts.join(' · ')
}
</script>

<template>
  <div
    v-if="visible"
    class="pointer-events-none absolute bottom-4 right-4 z-[5] w-80 max-w-[calc(100%-2rem)] transition-[right] duration-slower ease-panel"
    :class="ui.drawer.right.isOpen && 'xl:right-[calc(100%/3_+_1rem)]'"
  >
    <CollapsibleSection
      v-model:open="open"
      elevated
      expands-up
      body-class="p-0"
      class="pointer-events-auto"
      :aria-label="t('syncActivity.title')"
      @mouseenter="pauseHide"
      @mouseleave="resumeHide"
    >
      <template #header>
        <div class="flex min-w-0 flex-1 items-center gap-2.5">
          <i
            v-if="store.current"
            class="shrink-0 text-sm pi-spin"
            :class="[SYNC_STATUS_VISUAL.in_progress.icon, SYNC_STATUS_VISUAL.in_progress.tone]"
            aria-hidden="true"
          />
          <i
            v-else-if="store.recentlySettled"
            class="shrink-0 text-sm"
            :class="[SYNC_STATUS_VISUAL[store.recentlySettled.status].icon, SYNC_STATUS_VISUAL[store.recentlySettled.status].tone]"
            aria-hidden="true"
          />
          <div class="min-w-0 flex-1">
            <template v-if="store.current">
              <div class="flex items-center gap-2">
                <span class="truncate text-sm font-semibold text-color">{{ store.current.title }}</span>
                <span
                  v-if="moreActive > 0"
                  class="shrink-0 rounded-full bg-surface-hover px-1.5 text-xs leading-5 text-secondary"
                >
                  {{ t('syncActivity.moreActive', { count: moreActive }) }}
                </span>
              </div>
              <div class="mt-1.5 flex items-center gap-2">
                <ProgressBar :value="percent(store.current)" :show-value="false" class="flex-1" style="height: 4px" />
                <span class="shrink-0 text-xs tabular-nums text-secondary">
                  {{ store.current.acked_count }}/{{ store.current.target_count }}
                </span>
              </div>
            </template>
            <template v-else-if="store.recentlySettled">
              <div class="truncate text-sm font-semibold text-color">{{ store.recentlySettled.title }}</div>
              <div class="truncate text-xs text-muted">
                {{ t(`syncActivity.status.${store.recentlySettled.status}`) }} · {{ meta(store.recentlySettled) }}
              </div>
            </template>
          </div>
        </div>
      </template>

      <div class="flex items-center justify-between px-3 pt-2 pb-1">
        <span class="text-xs font-semibold uppercase tracking-wide text-muted">{{ t('syncActivity.history') }}</span>
        <RouterLink :to="{ name: 'SyncLog' }" class="text-xs text-primary hover:underline">{{ t('syncActivity.viewAll') }}</RouterLink>
      </div>
      <ul class="max-h-80 overflow-y-auto divide-y divide-border">
        <li v-for="a in store.items" :key="a.id" class="flex items-start gap-2.5 px-3 py-2">
          <i
            class="mt-0.5 shrink-0 text-sm"
            :class="[SYNC_STATUS_VISUAL[a.status].icon, SYNC_STATUS_VISUAL[a.status].tone, a.status === 'in_progress' && 'pi-spin']"
            :title="t(`syncActivity.status.${a.status}`)"
          />
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-1.5">
              <i class="shrink-0 text-xs text-muted" :class="SYNC_KIND_ICON[a.kind]" :title="t(`syncActivity.kind.${a.kind}`)" />
              <span class="truncate text-sm text-color">{{ a.title }}</span>
            </div>
            <ProgressBar
              v-if="a.status === 'in_progress'"
              :value="percent(a)"
              :show-value="false"
              class="mt-1"
              style="height: 3px"
            />
            <div class="mt-0.5 truncate text-xs text-muted">{{ meta(a) }}</div>
          </div>
          <span class="shrink-0 text-xs text-muted">{{ ago(a.started_at) }}</span>
        </li>
      </ul>
    </CollapsibleSection>
  </div>
</template>
